<template>
	<div class="docs-vault-mobile">
		<div class="vault-header">
			<div v-if="path.length > 0" class="back-navigation" @click="navigateBack">
				<component :is="getLucideIcon('ChevronLeft')" :size="20" :stroke-width="2" class="mr-2" />
				<span class="current-folder-name">{{ getCurrentFolderName() }}</span>
			</div>
			<h4 v-else class="vault-title">{{ FormMSG(1, 'My vault') }}</h4>
		</div>

		<div class="search-container">
			<b-input-group>
				<b-form-input
					v-model="toSearch"
					type="text"
					:placeholder="FormMSG(2, 'Type filename to search')"
					:disabled="disableActions"
					@keyup.enter="searchFile"
					@change="searchFile"
				/>
				<b-input-group-append>
					<b-input-group-text>
						<component :is="getLucideIcon(ICONS.SEARCH.name)" v-if="toSearch.length === 0" color="#06263E" :size="16" :stroke-width="2.5" />
						<component :is="getLucideIcon(ICONS.X.name)" v-else class="text-danger" :size="16" :stroke-width="2.5" @click="emptySearchBar" />
					</b-input-group-text>
				</b-input-group-append>
			</b-input-group>
		</div>

		<!-- Loading State -->
		<div v-if="isDataLoading" class="text-center p-4">
			<b-spinner class="align-middle" />
			<div class="mt-2">{{ FormMSG(4, 'Loading...') }}</div>
		</div>

		<!-- Documents List -->
		<div v-else class="documents-list">
			<div v-if="currentFolder.length === 0" class="empty-state">
				{{ FormMSG(3, 'No corresponding documents') }}
			</div>
			<div v-else class="document-items">
				<div
					v-for="item in sortedCurrentFolder"
					:key="item.Name"
					class="document-item"
					:class="{ 'is-folder': item.Type === DOCS_VAULT_CHILD_TYPE.FOLDER }"
					@click="openFolder(item)"
				>
					<div class="item-left">
						<!-- Checkbox -->
						<div class="item-checkbox" @click.stop>
							<b-form-checkbox v-model="item.selected" :disabled="disableActions" @change="(checked) => updateSelectedRows(item, checked)" />
						</div>

						<!-- Icon and Name -->
						<div class="item-info">
							<div class="item-icon">
								<svg
									v-if="item.Type === DOCS_VAULT_CHILD_TYPE.FOLDER"
									xmlns="http://www.w3.org/2000/svg"
									width="28"
									height="28"
									viewBox="0 0 24 24"
									fill="#77767b"
								>
									<path
										d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z"
									/>
								</svg>
								<svg
									v-else-if="item.Name.toLowerCase().includes('.pdf')"
									width="28"
									height="28"
									viewBox="0 0 35 35"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M4.2 0C1.88041 0 0 1.88041 0 4.2V30.8C0 33.1196 1.88041 35 4.2 35H30.8C33.1196 35 35 33.1196 35 30.8V4.2C35 1.88041 33.1196 0 30.8 0H4.2ZM6.4314 22.0791C6.5735 22.1764 6.80886 22.225 7.13747 22.225C7.46607 22.225 7.70143 22.1764 7.84352 22.0791C7.98562 21.9818 8.07444 21.8579 8.10996 21.7076C8.14549 21.5484 8.16325 21.3847 8.16325 21.2167V19.4787H9.86845C10.2415 19.4787 10.6189 19.4123 11.0008 19.2796C11.3827 19.1381 11.7335 18.9347 12.0533 18.6693C12.3819 18.3952 12.6439 18.0546 12.8392 17.6478C13.0435 17.2409 13.1456 16.7677 13.1456 16.2282C13.1456 15.6798 13.0435 15.2021 12.8392 14.7953C12.6439 14.3884 12.3819 14.0479 12.0533 13.7737C11.7335 13.4906 11.3827 13.2828 11.0008 13.1501C10.6278 13.0174 10.2548 12.9511 9.88177 12.9511H7.15079C6.82218 12.9511 6.58682 12.9998 6.44473 13.097C6.30263 13.1943 6.21381 13.3226 6.17829 13.4818C6.14276 13.6322 6.125 13.7914 6.125 13.9594V21.2034C6.125 21.3715 6.14276 21.5351 6.17829 21.6943C6.21381 21.8535 6.29818 21.9818 6.4314 22.0791ZM9.88177 17.462H8.16325V14.9677H9.86845C10.1171 14.9677 10.3347 15.0208 10.5212 15.127C10.7166 15.2331 10.8676 15.379 10.9742 15.5648C11.0808 15.7505 11.134 15.9672 11.134 16.2149C11.134 16.5068 11.0674 16.7456 10.9342 16.9313C10.8099 17.1171 10.65 17.2542 10.4546 17.3426C10.2681 17.4222 10.0772 17.462 9.88177 17.462ZM14.327 22.0791C14.4691 22.1764 14.6956 22.225 15.0065 22.225H17.3778C18.035 22.225 18.6389 22.1056 19.1895 21.8668C19.7491 21.628 20.2375 21.3007 20.6549 20.885C21.0724 20.4604 21.3965 19.9651 21.6274 19.399C21.8584 18.8241 21.9738 18.205 21.9738 17.5416C21.9738 16.8871 21.8584 16.2812 21.6274 15.724C21.3965 15.1668 21.0724 14.6803 20.6549 14.2646C20.2375 13.8489 19.7535 13.526 19.2029 13.2961C18.6522 13.0661 18.0572 12.9511 17.4177 12.9511H15.0198C14.6912 12.9423 14.4558 12.9865 14.3137 13.0838C14.1716 13.1811 14.0828 13.3093 14.0473 13.4685C14.0117 13.6189 13.994 13.7781 13.994 13.9462V21.2034C13.994 21.3803 14.0162 21.5484 14.0606 21.7076C14.105 21.8579 14.1938 21.9818 14.327 22.0791ZM17.4177 20.1951H16.0189V14.9677H17.4044C17.8929 14.9677 18.3281 15.0827 18.7099 15.3127C19.0918 15.5338 19.3938 15.8434 19.6158 16.2414C19.8379 16.6394 19.9489 17.0905 19.9489 17.5947C19.9489 18.0988 19.8379 18.5455 19.6158 18.9347C19.3938 19.3239 19.0918 19.6334 18.7099 19.8634C18.3281 20.0845 17.8973 20.1951 17.4177 20.1951ZM23.3325 22.0923C23.4746 22.1808 23.7055 22.225 24.0252 22.225C24.3627 22.225 24.5981 22.1808 24.7313 22.0923C24.8734 21.995 24.9622 21.8712 24.9977 21.7208C25.0332 21.5616 25.051 21.398 25.051 21.2299V18.4305H27.7687C27.9552 18.4305 28.115 18.4173 28.2483 18.3907C28.3904 18.3642 28.5014 18.2934 28.5813 18.1784C28.6612 18.0546 28.7012 17.8512 28.7012 17.5682C28.7012 17.2763 28.6612 17.0684 28.5813 16.9446C28.5014 16.8208 28.3904 16.7456 28.2483 16.719C28.115 16.6925 27.9596 16.6792 27.782 16.6792H25.051V14.9677H27.6742C27.8518 14.9677 28.0161 14.9545 28.1671 14.9279C28.327 14.8926 28.4558 14.8041 28.5535 14.6626C28.6512 14.5211 28.7 14.2911 28.7 13.9727C28.7 13.6366 28.6512 13.3978 28.5535 13.2563C28.4647 13.1147 28.3448 13.0307 28.1938 13.0042C28.0428 12.9688 27.8785 12.9511 27.7009 12.9511H24.0652C23.6744 12.9511 23.4035 13.0219 23.2525 13.1634C23.1016 13.3049 23.0261 13.5703 23.0261 13.9594V21.2167C23.0261 21.3847 23.0394 21.5484 23.066 21.7076C23.1016 21.8668 23.1904 21.995 23.3325 22.0923Z"
										fill="#EA3434"
									/>
								</svg>
								<svg
									v-else-if="item.Name.toLowerCase().includes('.xlsx')"
									width="28"
									height="28"
									viewBox="0 0 35 35"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M4.2 0C1.8804 0 0 1.88041 0 4.2V30.8C0 33.1196 1.8804 35 4.2 35H30.8C33.1196 35 35 33.1196 35 30.8V4.2C35 1.88041 33.1196 0 30.8 0H4.2ZM22.2705 24.3132C22.4887 24.2362 22.7453 24.0694 23.0405 23.8128C23.4768 23.4406 23.6886 23.1069 23.6758 22.8117C23.6758 22.5166 23.4961 22.1701 23.1367 21.7722L19.4325 17.4467L23.1367 13.129C23.4961 12.744 23.6758 12.3975 23.6758 12.0895C23.6886 11.7815 23.4768 11.4414 23.0405 11.0693C22.7582 10.7998 22.5079 10.6265 22.2897 10.5495C22.0716 10.4725 21.8598 10.4853 21.6545 10.588C21.4492 10.6907 21.2246 10.8896 20.9807 11.1847L17.5254 15.2197L14.07 11.1847C13.8262 10.8896 13.6016 10.6907 13.3963 10.588C13.1909 10.4853 12.9792 10.4725 12.761 10.5495C12.5428 10.6265 12.2926 10.7998 12.0102 11.0693C11.5868 11.4414 11.375 11.7815 11.375 12.0895C11.375 12.3975 11.5547 12.744 11.914 13.129L15.6182 17.4467L11.914 21.7722C11.5547 22.1701 11.375 22.5166 11.375 22.8117C11.375 23.1069 11.5868 23.4406 12.0102 23.8128C12.3054 24.0694 12.5621 24.2362 12.7802 24.3132C12.9984 24.3903 13.2102 24.3774 13.4155 24.2748C13.6208 24.1721 13.839 23.9796 14.07 23.6972L17.5254 19.6697L20.9807 23.6972C21.2246 23.9796 21.4427 24.1721 21.6353 24.2748C21.8406 24.3774 22.0523 24.3903 22.2705 24.3132Z"
										fill="#01AC49"
									/>
								</svg>
								<svg
									v-else-if="item.Name.toLowerCase().includes('.docx')"
									width="28"
									height="28"
									viewBox="0 0 35 35"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path d="M30.667 2H9.333A1.333 1.333 0 0 0 8 3.333V9l12 3.5L32 9V3.333A1.333 1.333 0 0 0 30.667 2z" fill="#41a5ee" />
									<path d="M32 9H8v7l12 3.5L32 16z" fill="#2b7cd3" />
									<path d="M32 16H8v7l12 3.5L32 23z" fill="#185abd" />
									<path d="M32 23H8v5.667A1.333 1.333 0 0 0 9.333 30h21.334A1.333 1.333 0 0 0 32 28.667z" fill="#103f91" />
									<path d="M16.667 7H8v19h8.667A1.337 1.337 0 0 0 18 24.667V8.333A1.337 1.337 0 0 0 16.667 7z" opacity=".1" />
									<path d="M15.667 8H8v19h7.667A1.337 1.337 0 0 0 17 25.667V9.333A1.337 1.337 0 0 0 15.667 8z" opacity=".2" />
									<path d="M15.667 8H8v17h7.667A1.337 1.337 0 0 0 17 23.667V9.333A1.337 1.337 0 0 0 15.667 8z" opacity=".2" />
									<path d="M14.667 8H8v17h6.667A1.337 1.337 0 0 0 16 23.667V9.333A1.337 1.337 0 0 0 14.667 8z" opacity=".2" />
									<path
										d="M1.333 8h13.334A1.333 1.333 0 0 1 16 9.333v13.334A1.333 1.333 0 0 1 14.667 24H1.333A1.333 1.333 0 0 1 0 22.667V9.333A1.333 1.333 0 0 1 1.333 8z"
										fill="#185abd"
									/>
									<path d="M11.95 21h-1.8l-2.1-6.9-2.2 6.9h-1.8l-2-10h1.8l1.4 7 2.1-6.8h1.5l2 6.8 1.4-7h1.7z" fill="#fff" />
									<path d="M0 0h32v32H0z" fill="none" />
								</svg>
								<component :is="getLucideIcon('FileImage')" v-else color="#06263E" :size="28" :stroke-width="1.5" />
							</div>
							<div class="item-name">
								{{ item.Name }}
								<div v-if="toSearch.length > 2 && item.parents" class="item-location">
									{{ item.parents }}
								</div>
							</div>
						</div>
					</div>

					<!-- Action Button -->
					<div class="item-right">
						<b-button variant="link" class="action-button" :disabled="disableActions" @click.stop.prevent="downloadItem(item)">
							<b-spinner v-if="itemToDownload && item.Name.toLowerCase() === itemToDownload.toLowerCase()" small />
							<component :is="getLucideIcon('Download')" v-else color="#06263E" :size="20" :stroke-width="2" />
						</b-button>
					</div>
				</div>
			</div>
		</div>

		<!-- Bottom Action Buttons -->
		<b-row class="footer-fixed">
			<b-col cols="12">
				<b-button
					v-if="selectedRows.length === 0"
					variant="primary"
					block
					:disabled="disableActions"
					style="font-size: 16px; font-weight: 700; border-radius: 12px"
					@click="initVault"
				>
					<component :is="getLucideIcon('RefreshCw')" :size="16" class="mr-2" />
					{{ FormMSG(9, 'Refresh data') }}
				</b-button>
				<b-button
					v-else
					variant="blue-sapphire"
					block
					:disabled="disableActions"
					style="font-size: 16px; font-weight: 700; border-radius: 12px"
					@click="downloadMultiple"
				>
					<b-spinner v-if="isDownloadMultiple" class="mr-2" small />
					<component :is="getLucideIcon('Download')" v-else :size="16" class="mr-2" />
					{{ FormMSG(12, 'Download selection') }} ({{ selectedRows.length }})
				</b-button>
			</b-col>
		</b-row>
	</div>
</template>
<script>
import languageMessages from '@/mixins/languageMessages';
import GlobalMixin from '@/mixins/global.mixin';
import { getAmazoneS3TreeAsJsonString } from '@/modules/document-package/cruds/document.crud';
import { DOCS_VAULT_CHILD_TYPE, DOCS_VAULT_DOWNLOAD_PATH } from '@/shared/constants';

export default {
	name: 'DocumentsVaultMobile',
	mixins: [languageMessages, GlobalMixin],
	data() {
		return {
			searchMode: {
				single: 'single',
				multiple: 'multiple'
			},
			toSearch: '',
			userNationalSecNumber: '',
			path: [],
			vaultData: [],
			currentFolder: [],
			lastOpenedFolder: [],
			selectedRows: [],
			isSelectedAll: false,
			isDataLoading: false,
			isDownloadMultiple: false,
			itemToDownload: '',
			DOCS_VAULT_CHILD_TYPE
		};
	},
	computed: {
		sortedCurrentFolder() {
			return [...this.currentFolder].sort(this.customSort);
		},
		disableActions() {
			return this.itemToDownload !== '' || this.isDownloadMultiple;
		}
	},
	async mounted() {
		this.itemToDownload = '';
		await this.initVault();
	},
	methods: {
		resetTableActions(resetSearch = false) {
			this.selectedRows = [];
			this.isSelectedAll = false;
			if (resetSearch) this.toSearch = '';
		},
		mapDocsWithSelectedState(docs, selectedState = false) {
			return docs.map((doc) => {
				return { ...doc, selected: selectedState };
			});
		},
		async initVault(isRefresh = false) {
			this.isDataLoading = true;
			this.resetTableActions(true);
			try {
				const result = await getAmazoneS3TreeAsJsonString();
				if (result && result.Children && result.Children.length > 0) {
					this.userNationalSecNumber = result.Children[0].Name;
					if (result.Children[0].Children && result.Children[0].Children.length > 0) {
						this.vaultData = result.Children[0].Children;
						this.currentFolder = this.lastOpenedFolder = this.mapDocsWithSelectedState(result.Children[0].Children);
					}
					if (isRefresh) {
						this.lastOpenedFolder = [];
						this.path = [];
					}
				}
			} catch (err) {
				console.error('Failed to init Vault Data', err);
				this.createToastForMobile(this.FormMSG(7, 'Error'), this.FormMSG(8, 'Failed to init Vault Data'), '', 'danger');
			} finally {
				this.isDataLoading = false;
			}
		},
		openFolder(item) {
			if (item.Type === DOCS_VAULT_CHILD_TYPE.FOLDER && !this.disableActions) {
				this.resetTableActions(true);
				this.isDataLoading = true;
				this.currentFolder = this.lastOpenedFolder = this.mapDocsWithSelectedState(item.Children);
				this.path.push(item.Name);
				this.isDataLoading = false;
			}
		},
		locationChange(location) {
			const nextFolder = this.searchInNode(this.vaultData, location);
			if (nextFolder) {
				this.currentFolder = this.lastOpenedFolder = this.mapDocsWithSelectedState(nextFolder.Children);
			} else {
				this.currentFolder = this.lastOpenedFolder = this.mapDocsWithSelectedState(this.vaultData);
			}
		},
		navigateBack() {
			this.resetTableActions(true);
			this.isDataLoading = true;
			this.path.pop();
			if (this.path.length > 0) {
				this.locationChange(this.path[this.path.length - 1]);
			} else {
				this.currentFolder = this.lastOpenedFolder = this.mapDocsWithSelectedState(this.vaultData);
			}
			this.isDataLoading = false;
		},
		emptySearchBar() {
			this.resetTableActions(true);
			this.currentFolder = this.mapDocsWithSelectedState(this.lastOpenedFolder);
		},
		searchFile() {
			this.isDataLoading = true;
			this.resetTableActions(false);
			if (this.toSearch) {
				const searchRes = this.searchInNode(this.lastOpenedFolder, this.toSearch, this.searchMode.multiple);
				searchRes ? (this.currentFolder = this.mapDocsWithSelectedState(searchRes)) : (this.currentFolder = []);
			} else {
				this.currentFolder = this.mapDocsWithSelectedState(this.lastOpenedFolder);
			}
			this.isDataLoading = false;
		},
		searchInNode(nodes, targetName, mode = 'single') {
			const stack = [...nodes];
			const multipleResults = [];
			const formattedTarget = targetName.replace(/[.,*+?^${}()|[\]\\]/g, '');
			if (formattedTarget) {
				const patternForMultiSearch = new RegExp(formattedTarget, 'i');

				while (stack.length > 0) {
					const node = stack.pop();

					if (mode === this.searchMode.multiple && patternForMultiSearch.test(node.Name) && node.Type === DOCS_VAULT_CHILD_TYPE.FILE) {
						multipleResults.push(node);
					} else if (mode === this.searchMode.single && node.Name.toLowerCase() === targetName.toLowerCase()) {
						return node;
					}

					if (node.Type === DOCS_VAULT_CHILD_TYPE.FOLDER) {
						stack.push(...node.Children.map((i) => ({ ...i, parents: node.parents ? `${node.parents}${node.Name}/` : `${node.Name}/` })));
					}
				}
				if (mode === this.searchMode.multiple && multipleResults.length > 0) return multipleResults;
			}
			return null;
		},
		updateSelectedRows(item, checked) {
			if (checked) {
				this.selectedRows.push(item);
			} else {
				this.selectedRows = this.selectedRows.filter((elem) => elem.Name !== item.Name);
			}
			this.isSelectedAll = this.selectedRows.length === this.currentFolder.length;
		},
		setSelectionForAll(checked) {
			if (checked) {
				this.currentFolder = this.mapDocsWithSelectedState(this.currentFolder, true);
				this.selectedRows = [...this.currentFolder];
			} else {
				this.currentFolder = this.mapDocsWithSelectedState(this.currentFolder);
				this.selectedRows = [];
			}
		},
		async downloadItem(item) {
			this.itemToDownload = item.Name;
			try {
				const param = `${this.userNationalSecNumber}/${this.path.length > 0 ? this.path.join('/') + '/' : ''}${item.Name}`;
				const path = item.Type === DOCS_VAULT_CHILD_TYPE.FILE ? DOCS_VAULT_DOWNLOAD_PATH.FILE : DOCS_VAULT_DOWNLOAD_PATH.FOLDER;
				const filename = item.Type === DOCS_VAULT_CHILD_TYPE.FILE ? item.Name : `${item.Name}_.zip`;
				await this.forceDownload(param, filename, path, { useParam: true, paramKey: 'key' });
			} catch (error) {
				console.log('error on download document in Vault >>>> ', error);
				this.createToastForMobile(this.FormMSG(7, 'Error'), this.FormMSG(11, 'Some issues occured during the download process'), '', 'danger');
			} finally {
				this.itemToDownload = '';
			}
		},
		provideMultipleParam(folder) {
			let param = '';
			if (this.toSearch.length > 0) {
				this.selectedRows.forEach((row, index) => {
					param += `${this.userNationalSecNumber}/${row.parents}${row.Name}${index !== this.selectedRows.length - 1 ? ',' : ''}`;
				});
			} else {
				if (this.isSelectedAll) {
					param = folder;
				} else {
					this.selectedRows.forEach((row, index) => {
						param += `${folder}${row.Name}${index !== this.selectedRows.length - 1 ? ',' : ''}`;
					});
				}
			}
			return param;
		},
		async downloadMultiple() {
			this.isDownloadMultiple = true;
			try {
				const currentOpenedFolder = `${this.userNationalSecNumber}/${this.path.length > 0 ? this.path.join('/') + '/' : ''}`;
				const param = this.provideMultipleParam(currentOpenedFolder);
				const path = DOCS_VAULT_DOWNLOAD_PATH.FOLDER;
				const filename = `Documents_${new Date().toISOString().replace('T', '_').split('.')[0]}_.zip`;
				await this.forceDownload(param, filename, path, { useParam: true, paramKey: 'key' });
				this.setSelectionForAll(false);
				this.isSelectedAll = false;
			} catch (error) {
				console.log('error on download multiples documents in Vault >>>> ', error);
				this.createToastForMobile(this.FormMSG(7, 'Error'), this.FormMSG(11, 'Some issues occured during the download process'), '', 'danger');
			} finally {
				this.isDownloadMultiple = false;
			}
		},
		customSort(a, b) {
			const aIsFolder = a.Type === DOCS_VAULT_CHILD_TYPE.FOLDER;
			const bIsFolder = b.Type === DOCS_VAULT_CHILD_TYPE.FOLDER;

			if (aIsFolder && !bIsFolder) return -1;
			if (!aIsFolder && bIsFolder) return 1;

			return a.Name.toLowerCase().localeCompare(b.Name.toLowerCase());
		},
		getCurrentFolderName() {
			return this.path.length > 0 ? this.path[this.path.length - 1] : '';
		}
	}
};
</script>

<style lang="scss" scoped>
.docs-vault-mobile {
	padding: 1rem;
	height: 100vh;
	display: flex;
	flex-direction: column;
}

.vault-header {
	margin-bottom: 1rem;

	.back-navigation {
		display: flex;
		align-items: center;
		cursor: pointer;
		padding: 0.5rem 0;
		font-size: 1.1rem;
		font-weight: 600;
		color: #495057;

		&:hover {
			color: #007bff;
		}

		.current-folder-name {
			margin-left: 0.5rem;
		}
	}

	.vault-title {
		margin: 0;
		font-size: 1.5rem;
		font-weight: 600;
		color: #495057;
	}
}

.search-container {
	margin-bottom: 1rem;
	padding: 0 0.5rem;

	.input-group {
		border-radius: 0.5rem;
		overflow: hidden;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
	}

	.form-control {
		border: none;
		padding: 0.75rem 1rem;
		font-size: 1rem;

		&:focus {
			box-shadow: none;
			border-color: #007bff;
		}
	}

	.input-group-text {
		background-color: white;
		border: none;
		padding: 0.75rem;
	}
}

.documents-list {
	flex: 1;
	overflow-y: auto;
	margin-bottom: 5rem;

	.empty-state {
		text-align: center;
		padding: 3rem 1rem;
		color: #6c757d;
		font-size: 1.1rem;
	}
}

.document-items {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.5rem 0.5rem 0;
}

.document-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: white;
	border-radius: 0.5rem;
	padding: 1rem;
	box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
	transition: all 0.2s ease;
	min-height: 60px;

	&:hover {
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
		transform: translateY(-1px);
	}

	&.is-folder {
		cursor: pointer;

		&:hover {
			background-color: #f8f9fa;
		}
	}
}

.item-left {
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
}

.item-checkbox {
	margin-right: 1rem;
	flex-shrink: 0;
}

.item-info {
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
}

.item-icon {
	margin-right: 1rem;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.item-name {
	flex: 1;
	min-width: 0;
	font-size: 1rem;
	font-weight: 500;
	color: #495057;

	.item-location {
		font-size: 0.85rem;
		color: #6c757d;
		margin-top: 0.25rem;
		font-weight: 400;
	}
}

.item-right {
	flex-shrink: 0;
	margin-left: 1rem;
}

.action-button {
	padding: 0.5rem;
	border: none;
	background: transparent;

	&:hover {
		background-color: #f8f9fa;
		border-radius: 0.25rem;
	}

	&:disabled {
		opacity: 0.6;
	}
}

.footer-fixed {
	padding: 12px 15px 12px 15px;
	box-shadow: 0px -4px 8px rgba(6, 38, 62, 0.08);
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: white;
	z-index: 1000;
}

// Responsive adjustments
@media (max-width: 576px) {
	.docs-vault-mobile {
		padding: 0.5rem;
	}

	.document-item {
		padding: 0.75rem;
	}

	.item-name {
		font-size: 0.9rem;

		.item-location {
			font-size: 0.8rem;
		}
	}
}
</style>
